import { Controller } from 'react-hook-form';
import type { Control, UseFormClearErrors } from 'react-hook-form';

import Input from 'components/partials/input/input';
import Textarea from 'components/partials/textarea/textarea';
import Select from 'components/partials/select/select';

export interface ProductFormValues {
  name: string;
  description: string;
  type: string;
}

export interface ProductTypeOption {
  name: string;
  description: string;
}

interface ProductFormProps {
  control: Control<ProductFormValues>;
  disabled: boolean;
  productTypes: ProductTypeOption[];
  clearErrors: UseFormClearErrors<ProductFormValues>;
}

const ProductForm = ({
  control,
  disabled,
  productTypes,
  clearErrors,
}: ProductFormProps): React.JSX.Element => (
  <div className="flex h-full gap-8 items-baseline">
    <div className="flex flex-col gap-3 w-1/2">
      <Controller
        name="name"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => (
          <Input
            {...field}
            label="Product name"
            placeholder="Product name..."
            disabled={disabled}
            errorsNames={error}
            labelInTop={true}
            required={true}
            data-testid="product-name-input"
          />
        )}
      />
      <Controller
        name="type"
        control={control}
        rules={{ required: 'This field is required' }}
        render={({ field, fieldState: { error } }) => {
          const transformedOptions = productTypes.map((pt) => ({
            value: pt.name,
            label: pt.description,
          }));

          return (
            <Select
              {...field}
              label="Product type"
              disabled={disabled}
              options={transformedOptions}
              labelInTop={true}
              fieldError={
                error && { ...error, message: 'This field is required' }
              }
              required={true}
              onChange={(selectedOption) => {
                field.onChange(
                  (selectedOption as { value: string } | null)?.value
                );
              }}
              value={field.value}
              clearInputErr={() => clearErrors('type')}
              data-testid="product-type-select"
            />
          );
        }}
      />
    </div>

    <div className="flex flex-col gap-3 w-1/2">
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label="Product description"
            placeholder="Product description..."
            disabled={disabled}
            data-testid="product-description-textarea"
          />
        )}
      />
    </div>
  </div>
);

export default ProductForm;
